import React from 'react';
import { Button, KIND, SIZE } from 'baseui/button';
import { ERASED_KEY_STATUSES, IDENTITY_TYPE, IDENTITY_TYPE_LABEL, KEY_STATUS, MESSAGES } from '../constants';
import { Candidate, IdentityType, SearchResult } from '../types';
import useStyles, { STYLES, mergeStyles } from '../useStyles';
import { formatDate } from '../utils/format';
import Badge, { KeyStatusBadge } from './common/Badge';
import Notice from './common/Notice';

interface ICandidateListProps {
	result: SearchResult;
	identityType: IdentityType;
	canReview: boolean;
	isLoading: boolean;
	onReview: (candidate: Candidate) => void;
}

const CandidateList: React.FC<ICandidateListProps> = ({ result, identityType, canReview, isLoading, onReview }) => {
	const { css, classes } = useStyles();
	const { candidates, sharedIdentity, truncated, cap } = result;
	const typeLabel: string = IDENTITY_TYPE_LABEL[identityType] || identityType;

	if (!candidates.length) {
		return (
			<div className={classes.emptyState}>
				<h3 className={classes.sectionTitle}>{MESSAGES.EMPTY_STATE}</h3>
				<p className={classes.muted}>{MESSAGES.EMPTY_STATE_HINT}</p>
			</div>
		);
	}

	const accountCount: string = truncated ? `more than ${cap}` : String(candidates.length);
	// UID search returns the ID even without a key record, so a typo is not mistaken for "we hold no data".
	// That is not yet a data issue, so it gets a neutral label rather than the red "No key" badge.
	const isUnmappedUid = (candidate: Candidate): boolean =>
		identityType === IDENTITY_TYPE.UID && candidate.keyStatus === KEY_STATUS.NO_KEY;

	return (
		<section aria-label='Select account' className={classes.stack}>
			<h3 className={classes.sectionTitle}>Select the account to review</h3>
			{sharedIdentity && (
				<Notice tone='warning' title='Shared identifier.'>
					{`This ${typeLabel} is linked to ${accountCount} accounts — confirm the right account with the requester before reviewing.`}
					{truncated && ` Showing first ${cap}; the identifier is shared by more accounts (often a placeholder value).`}
				</Notice>
			)}
			{candidates.some(isUnmappedUid) && (
				<Notice tone='neutral' title='No key record.'>
					{MESSAGES.UNMAPPED_UID}
				</Notice>
			)}
			{!canReview && <Notice tone='neutral'>You do not have permission to review personal data for these accounts.</Notice>}
			<div className={classes.tableWrap}>
				<table className={classes.table}>
					<caption className={classes.srOnly}>{`Accounts matching this ${typeLabel}`}</caption>
					<thead>
						<tr>
							<th scope='col' className={classes.th}>
								UID
							</th>
							<th scope='col' className={classes.th}>
								Matched on
							</th>
							<th scope='col' className={classes.th}>
								Hashed at
							</th>
							<th scope='col' className={classes.th}>
								Key status
							</th>
							<th scope='col' className={css(mergeStyles(STYLES.th, STYLES.tdRight))}>
								<span className={classes.srOnly}>Action</span>
							</th>
						</tr>
					</thead>
					<tbody>
						{candidates.map((candidate) => (
							<tr key={candidate.uid}>
								<th scope='row' className={css(mergeStyles(STYLES.td, STYLES.tdUid))}>
									{candidate.uid}
								</th>
								<td className={classes.td}>{IDENTITY_TYPE_LABEL[candidate.matchedOn] || candidate.matchedOn}</td>
								<td className={css(mergeStyles(STYLES.td, STYLES.mono))}>{formatDate(candidate.hashedAt)}</td>
								<td className={classes.td}>
									{isUnmappedUid(candidate) ? (
										<Badge tone='neutral' label='No key record' />
									) : (
										<KeyStatusBadge keyStatus={candidate.keyStatus} />
									)}
									{ERASED_KEY_STATUSES.includes(candidate.keyStatus) && candidate.erasedAt && (
										<div className={classes.muted}>{`on ${formatDate(candidate.erasedAt)}`}</div>
									)}
								</td>
								<td className={css(mergeStyles(STYLES.td, STYLES.tdRight))}>
									<Button
										kind={KIND.secondary}
										size={SIZE.compact}
										disabled={!canReview || isLoading}
										onClick={() => onReview(candidate)}
										overrides={{
											BaseButton: { props: { 'aria-label': `Review data for UID ${candidate.uid}` } }
										}}>
										Review data
									</Button>
								</td>
							</tr>
						))}
					</tbody>
				</table>
			</div>
		</section>
	);
};

export default CandidateList;
